import { differenceInDays } from 'date-fns';
import { useMemo, useState, useEffect, useCallback } from 'react';

import { fg } from '@confluence/feature-gating';

import {
	useGetModernizeChangeboardingOnboardingState,
	useSetModernizeChangeboardingOnboardingState,
} from './useModernizeChangeboardingOnboardingState.ts';

// Number of days to wait before showing the modal
const THREE_WEEKS_DAYS = 21;

export const useEligibleForModernizeModal = () => {
	const { loading, error, hasDismissedModernizeConfluenceIpmModal, changeboardingStartedAt } =
		useGetModernizeChangeboardingOnboardingState();
	const [isOpen, setIsOpen] = useState<null | boolean>(null);
	const { completeModernizeConfluenceIpmModal } = useSetModernizeChangeboardingOnboardingState();
	const changeboardingStartedAtLeastThreeWeeksAgo = useMemo(
		() =>
			changeboardingStartedAt
				? differenceInDays(new Date(), changeboardingStartedAt) >= THREE_WEEKS_DAYS
				: false,
		[changeboardingStartedAt],
	);

	useEffect(() => {
		if (isOpen === null && !loading && !error) {
			setIsOpen(!hasDismissedModernizeConfluenceIpmModal);
		}
	}, [loading, error, hasDismissedModernizeConfluenceIpmModal, isOpen]);

	const onInteraction = useCallback(() => {
		if (!hasDismissedModernizeConfluenceIpmModal) {
			completeModernizeConfluenceIpmModal();
		}
	}, [hasDismissedModernizeConfluenceIpmModal, completeModernizeConfluenceIpmModal]);

	const onDismiss = useCallback(() => {
		setIsOpen(false);
		onInteraction();
	}, [onInteraction]);

	if (loading || error) {
		return { loading, error, eligible: false, onInteraction: () => {}, onDismiss: () => {} };
	}

	return {
		loading: false,
		error,
		eligible:
			isOpen &&
			changeboardingStartedAtLeastThreeWeeksAgo &&
			fg('cc_onboarding_modernize_confluence_ipm_modal'),
		onInteraction,
		onDismiss,
	};
};
